import { useMemo, useState } from "react";
import { OPERATION_LABEL, type Job, type JobStatus, type Operation, type TaskWithJob } from "../data/types.ts";

const STEP_SHORT: Record<Operation, string> = { pli: "Pli", encartage: "Enc.", piquage: "Piq.", encartage_piquage: "Enc.-piq.", dos_carre_colle: "DCC", rainage: "Rain.", rainage_pliage: "Rain.-pli", pelliculage: "Pellic." };
import { fmtDateTime, startOfWeek, addDays } from "../lib/time.ts";
import { groupJobs } from "../lib/groups.ts";
import { Badge, Empty, ErrorBox, Loading, useApp, useData } from "../ui/kit.tsx";
import { Header } from "./Planning.tsx";

export const JOB_STATUS: Record<JobStatus, { label: string; tone: string }> = {
  a_planifier: { label: "À planifier", tone: "orange" },
  planifie: { label: "Planifié", tone: "blue" },
  en_cours: { label: "En cours", tone: "teal" },
  termine: { label: "Terminé", tone: "" },
  annule: { label: "Annulé", tone: "" },
};

const FILTERS: { key: string; label: string; match: (j: Job) => boolean }[] = [
  { key: "actifs", label: "En cours et à venir", match: (j) => j.status !== "termine" && j.status !== "annule" },
  { key: "a_planifier", label: "À planifier", match: (j) => j.status === "a_planifier" },
  { key: "en_cours", label: "En cours", match: (j) => j.status === "en_cours" },
  { key: "termine", label: "Terminés", match: (j) => j.status === "termine" },
  { key: "tous", label: "Tous", match: () => true },
];

export function JobsPage() {
  const { api, can } = useApp();
  const [filter, setFilter] = useState("actifs");
  const [q, setQ] = useState("");
  const since = useMemo(() => addDays(startOfWeek(new Date()), -28), []);
  const { data, error, loading } = useData(api, async () => {
    const [jobs, tasks] = await Promise.all([api.jobs(), api.tasks(since)]);
    return { jobs, tasks };
  }, [since], 20_000);
  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Loading />;
  const f = FILTERS.find((x) => x.key === filter)!;
  const needle = q.trim().toLowerCase();
  const rows = data.jobs.filter(f.match).filter((j) => !needle || `${j.of_number} ${j.client} ${j.title}`.toLowerCase().includes(needle));
  const byJob = new Map<string, TaskWithJob[]>();
  for (const t of data.tasks) byJob.set(t.job_id, [...(byJob.get(t.job_id) || []), t]);
  const groups = groupJobs(rows);
  return (
    <div className="page">
      <Header title="Jobs">
        <span className="spacer" />
        <input className="search" type="search" placeholder="Rechercher un OF, un client…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Rechercher" />
        {can("jobs") && <a className="btn btn-primary" href="#/jobs/nouveau">+ Nouveau job</a>}
      </Header>
      <div className="chips" role="tablist">
        {FILTERS.map((x) => (
          <button key={x.key} role="tab" aria-selected={filter === x.key} className={`chip ${filter === x.key ? "on" : ""}`} onClick={() => setFilter(x.key)}>
            {x.label} <span>{data.jobs.filter(x.match).length}</span>
          </button>
        ))}
      </div>
      {!data.jobs.length ? (
        <Empty title="Aucun job pour l'instant">Créez votre premier job : il apparaîtra dans « À planifier ».<div className="row-gap"><a className="btn btn-primary" href="#/jobs/nouveau">Créer un job</a></div></Empty>
      ) : !rows.length ? <Empty title={needle ? `Aucun job ne correspond à « ${q.trim()} »` : "Aucun job dans cette catégorie"} /> : (
        <div className="job-groups">
          {groups.map((g) => (
            <section key={g.key} className={`job-group jg-${g.key}`}>
              <h2 className="jg-head"><span className={`jg-dot ${g.tone ? "t-" + g.tone : ""}`} aria-hidden="true" />{g.label}<span className="jg-count">{g.rows.length}</span></h2>
              <div className="job-list card">
                {g.rows.map((j) => {
                  const ts = (byJob.get(j.id) || []).sort((a, b) => a.seq - b.seq);
                  const done = ts.filter((t) => t.status === "termine").length;
                  return (
                    <a key={j.id} className="job-row" href={`#/jobs/${j.id}`}>
                      <span className="jr-of">{j.of_number || "—"}</span>
                      <span className="jr-main"><b>{j.client}</b>{j.priority === "urgent" && <Badge tone="red">Urgent</Badge>}<small>{j.title}</small></span>
                      <span className="jr-steps" title={ts.map((t) => OPERATION_LABEL[t.operation]).join(" → ")}>
                        {ts.map((t) => <span key={t.id} className={`step st-${t.status}`}>{STEP_SHORT[t.operation]}</span>)}
                        {ts.length > 1 && <em>{done}/{ts.length}</em>}
                      </span>
                      <span className="jr-qty">{j.quantity.toLocaleString("fr-FR")} ex.</span>
                      <span className={`jr-due ${g.key === "retard" ? "late" : ""}`}>{fmtDateTime(j.due_at)}</span>
                      <span className="jr-status"><Badge tone={JOB_STATUS[j.status].tone}>{JOB_STATUS[j.status].label}</Badge></span>
                    </a>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
